import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useConfirm } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { BusyIcon } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { useNavPermission } from '../../../hooks/useNavPermission';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  DAILY_QC_FORM, DAILY_QC_NAV_ID, PERIODS, errorText, fetchHistory, fetchLogbook, isValidationError, ruleText,
  type DailyQcEntry, type DailyQcLogbookDetail,
} from './dailyQcApi';
import {
  STATUS_PILL, fmtNum, initialHistoryState, seriesFor, seriesStats, showValue, type DailyQcHistoryState,
} from './dailyQcFormat';
import { DailyQcTrendChart } from './DailyQcTrend';
import DailyQcEntryDialog from './DailyQcEntryDialog';
import { useDailyQcDelete } from './useDailyQcDelete';

interface Props {
  templateId: number;
  state: DailyQcHistoryState | null;
  onState: (s: DailyQcHistoryState) => void;
  onBack: () => void;
  onHome?: () => void;
}

/**
 * Level 2 — one logbook's history: its entries in a date range (one column per configured
 * field, failing values in red), the Pass/Fail counts and a trend chart of a numeric field.
 */
export default function DailyQcHistory({ templateId, state, onState, onBack, onHome }: Props) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const deleteEntries = useDailyQcDelete();
  const perm = useNavPermission(DAILY_QC_NAV_ID);

  const [detail, setDetail] = useState<DailyQcLogbookDetail | null>(null);
  const [entries, setEntries] = useState<DailyQcEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  // The range typed in the toolbar; applied on Refresh (and at once when a preset is picked).
  const [draft, setDraft] = useState<DailyQcHistoryState | null>(state);
  // The entry popup over the history (prototype) — oid null = New.
  const [editing, setEditing] = useState<{ oid: string | null } | null>(null);
  const onOpenEntry = (oid: string | null) => setEditing({ oid });

  const view = state ?? draft;

  const load = useCallback(async (range: DailyQcHistoryState | null) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const d = await fetchLogbook(templateId);
      setDetail(d);
      const r = range ?? initialHistoryState();
      let res;
      try {
        res = await fetchHistory(templateId, r.period ? { period: r.period } : { from: r.from, to: r.to });
      } catch (e) {
        if (!isValidationError(e)) throw e;
        // A From / To the API refused (G5's Date Picker messages): the shared validation
        // Warning, as every form shows validation; the rows stay until the range is fixed.
        notify.warning(errorText(e, 'Check the dates.'));
        return;
      }
      // A preset's From/To come back from the server (calendar months), so the boxes show them.
      const shown = r.period ? { ...r, from: res.range.from ?? '', to: res.range.to ?? '' } : r;
      onState(shown); setDraft(shown);
      setEntries(res.entries);
      setSelected(new Set());
    } catch (e) {
      notify.error(errorText(e, 'Failed to load the logbook history.'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [templateId, notify, onState]);

  // Load once on open with the range the view had (or the logbook's default).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(state); }, [templateId]);

  // Search + trend field live in `view`; only the range comes from the toolbar draft.
  const apply = useCallback((range: Pick<DailyQcHistoryState, 'from' | 'to' | 'period'>) => {
    if (!view) return;
    const next = { ...view, ...range };
    onState(next); setDraft(next); void load(next);
  }, [view, onState, load]);

  const pickPeriod = (label: string) => apply({ period: label, from: '', to: '' });

  const fields = useMemo(() => detail?.fields ?? [], [detail]);

  const rows = useMemo(() => {
    const q = (view?.search ?? '').trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => [...Object.values(e.values), e.remark, e.testedBy, e.enteredBy, e.status]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [entries, view?.search]);

  const pass = rows.filter((e) => e.status === 'Pass').length;
  const fail = rows.filter((e) => e.status === 'Fail').length;

  const labels = detail?.labels ?? null;
  const cols = useMemo<AppColumn<DailyQcEntry>[]>(() => [
    // G5 shows the Daily QC ID when the template lists it (M10).
    ...(labels?.showDailyQcId
      ? [{ field: 'dailyQcId', header: labels.dailyQcId, width: 100, text: (r: DailyQcEntry) => r.dailyQcId ?? '' } as AppColumn<DailyQcEntry>]
      : []),
    ...fields.map<AppColumn<DailyQcEntry>>((f) => ({
      field: `v_${f.name}`,
      header: <span title={f.accept ? `${f.caption} | pass when ${f.accept}` : f.caption}>{f.caption}</span>,
      headerText: f.caption,
      width: f.width || 100,
      align: f.type === 'Numeric' ? 'right' : f.type === 'Checkbox' ? 'center' : undefined,
      value: (r) => r.values[f.name],
      text: (r) => showValue(f, r.values[f.name]),
      render: (r) => {
        const t = showValue(f, r.values[f.name]);
        return r.accept[f.name] === false ? <span className="dqc-status-fail">{t}</span> : t;
      },
    })),
    { field: 'status', header: 'Status', width: 90, align: 'center', text: (r) => r.status,
      render: (r) => <span className={`adg-pill ${STATUS_PILL[r.status] ?? 'info'}`}>{r.status}</span> },
    { field: 'remark', header: labels?.remark ?? 'Remark', width: 220, text: (r) => r.remark ?? '' },
    { field: 'testedBy', header: labels?.testedBy ?? 'Tested By', width: 110, text: (r) => r.testedBy ?? '' },
    { field: 'enteredBy', header: 'Entered By', width: 110, text: (r) => r.enteredBy ?? '' },
    { field: 'dateEntered', header: 'Date Entered', width: 130, type: 'datetime' },
  ], [fields, labels]);

  // Freeze the leading frozen fields (the prototype pins e.g. Date + instrument).
  const sticky = useMemo(() => { let n = 0; while (n < fields.length && fields[n]!.freeze) n++; return n; }, [fields]);

  const numeric = fields.filter((f) => f.type === 'Numeric');
  const trendField = numeric.find((f) => f.name === view?.trendField) ?? numeric.find((f) => f.accept) ?? numeric[0] ?? null;
  const trendPoints = trendField ? seriesFor(trendField, rows) : [];
  const trendLimits = trendField ? detail?.limits[trendField.name] ?? [] : [];
  const stats = seriesStats(trendPoints);

  const selectedOid = selected.size === 1 ? String([...selected][0]) : null;
  const canNew = perm.canWrite && (detail?.isActive ?? false);

  const deleteSelected = useCallback(async () => {
    const oids = [...selected].map(String);
    if (oids.length === 0) { void alert({ title: 'Daily QC', message: 'Select the entries to delete first.', tone: 'warning' }); return; }
    await deleteEntries(oids);
    void load(view ?? null);   // also after a partial failure, so the grid shows what is left
  }, [selected, alert, deleteEntries, load, view]);

  const retrieve = () => { if (draft) apply({ from: draft.from, to: draft.to, period: draft.period }); };

  useHotkeys([
    { combo: 'esc', label: 'Back to logbooks', group: 'Daily QC', run: () => onBack(), allowInInput: false, disabled: !!editing },
    { combo: 'alt+r', label: 'Retrieve', group: 'Daily QC', run: retrieve, disabled: loading || !!editing },
  ]);

  const logbook = detail?.logbook;
  const meta = logbook
    ? [[logbook.instrumentCode, logbook.instrumentName].filter(Boolean).join(' '), logbook.category, logbook.method, logbook.schedule]
      .filter(Boolean).join('  |  ')
    : '';

  return (
    <div className="bl-page dqc-hist-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Analysis</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-link" onClick={onBack}>Daily QC</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{logbook?.name ?? ''}</span>
        </div>
        <FormTitle pageId="dailyqc" auditFormName={DAILY_QC_FORM} loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<button className="ca-btn" onClick={onBack} title="Back to logbooks (Esc)">‹ Back</button>}
            onNew={canNew ? () => onOpenEntry(null) : undefined}
            onEdit={perm.canRead ? () => { if (selectedOid) onOpenEntry(selectedOid); } : undefined}
            editDisabled={!selectedOid}
            onDelete={perm.canDelete ? () => void deleteSelected() : undefined}
            deleteDisabled={selected.size === 0}
            beforeRefresh={view && (
              <span className="dqc-range" data-unsaved-ignore>
                <label>From</label>
                <input type="date" className="dqc-date" value={draft?.from ?? ''} data-unsaved-ignore
                  onChange={(e) => draft && setDraft({ ...draft, from: e.target.value, period: '' })} />
                <label>To</label>
                <input type="date" className="dqc-date" value={draft?.to ?? ''} data-unsaved-ignore
                  onChange={(e) => draft && setDraft({ ...draft, to: e.target.value, period: '' })} />
                <select className="dqc-period" value={draft?.period ?? ''} data-unsaved-ignore
                  onChange={(e) => pickPeriod(e.target.value)} aria-label="Date range">
                  <option value="" disabled>—</option>
                  {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {/* "Retrieve", as the prototype (owner request 2026-09-28) — a deliberate exception
                    to the shared toolbar's "Refresh" label (standing rule 7), so this screen
                    renders its own button here instead of AppToolbar's onRefresh. */}
                <button className="ca-btn ca-btn-solid" onClick={retrieve} disabled={loading}
                  title={`Retrieve the entries for this date range (${formatCombo('alt+r')})`}>
                  <BusyIcon busy={loading} icon="ti-search" /> Retrieve
                </button>
              </span>
            )}
            exportData={{ filename: `daily-qc-${logbook?.name ?? templateId}`, ...gridExportMatrix(rows, cols) }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search entries…" value={view?.search ?? ''} data-unsaved-ignore
              onChange={(e) => view && onState({ ...view, search: e.target.value })} />
            {view?.search && <span className="list-search-clear" onClick={() => view && onState({ ...view, search: '' })}>×</span>}
          </div>
        </div>

        <div className="dqc-hist-head">
          <div>
            <div className="dqc-sectitle">{logbook?.name}</div>
            <div className="dqc-muted">{meta}</div>
          </div>
          <div className="dqc-stats">
            <div><b>{rows.length}</b><span>Entries</span></div>
            <div><b className="dqc-pass">{pass}</b><span>Pass</span></div>
            <div><b className="dqc-fail">{fail}</b><span>Fail</span></div>
            <div><b>{fmtLocalDate(logbook?.nextDue) || '—'}</b><span>Next due</span></div>
          </div>
        </div>
        {detail?.instructions && <div className="dqc-instructions">{detail.instructions}</div>}
        {detail && !detail.isActive && <div className="dqc-instructions">This logbook is not active, so it does not accept new entries.</div>}

        <div className="bl-card bl-card-flex dqc-hist-grid">
          <AppDataGrid<DailyQcEntry>
            rows={rows} columns={cols} getRowId={(r) => r.oid}
            selectable selectedIds={selected} onSelectionChange={setSelected}
            onRowDoubleClick={(r) => onOpenEntry(r.oid)}
            stickyColumns={sticky}
            loading={loading}
            emptyText="No entries in this date range. Click New to add one."
            footerNote="Double-click a row to open it."
          />
        </div>

        {numeric.length > 0 && (
          <div className="dqc-trend">
            <div className="dqc-trend-head">
              <div className="dqc-sectitle">Trend</div>
              <select className="dqc-period" value={trendField?.name ?? ''} aria-label="Trend field" data-unsaved-ignore
                onChange={(e) => view && onState({ ...view, trendField: e.target.value })}>
                {numeric.map((f) => <option key={f.name} value={f.name}>{f.caption}</option>)}
              </select>
              {stats && (
                <div className="dqc-trend-stats">
                  <span>n <b>{stats.n}</b></span>
                  <span>Mean <b>{fmtNum(stats.mean)}</b></span>
                  <span>SD <b>{fmtNum(Number(stats.sd.toPrecision(3)))}</b></span>
                  <span>Out of limit <b className={stats.out ? 'dqc-fail' : undefined}>{stats.out}</b></span>
                  {/* As the prototype: the field's own pass rule with its symbol — "CT≥15", "TEMP≥121",
                      "AM≥34.5 & AM≤35.5" (ruleText without "Pass: "). */}
                  {trendLimits.length > 0 && trendField && <span>Limit <b>{ruleText(trendField).replace('Pass: ', '')}</b></span>}
                </div>
              )}
            </div>
            <DailyQcTrendChart points={trendPoints} limits={trendLimits} />
            <div className="dqc-note">Follows the date range and search above. Red points failed the acceptance rule; dashed red lines are the limits.</div>
          </div>
        )}
      </div>

      {editing && (
        <DailyQcEntryDialog
          key={editing.oid ?? 'new'}
          templateId={templateId}
          oid={editing.oid}
          onSaved={() => { void load(view ?? null); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
